import type { TaxonomyField, TaxonomyFieldValue } from '@/entities/taxonomy';

export const EMPTY_VALUE: TaxonomyFieldValue = { value: '', code: '' };

export function isBlankValue(v: TaxonomyFieldValue): boolean {
  return !v.value.trim() && !v.code.trim();
}

/**
 * Truncates TRAILING blank rows to exactly one, so there is always somewhere
 * to type. Interior rows are never touched.
 *
 * The trailing-only rule is the whole point. This used to COMPACT — it built
 * the list of non-blank rows and re-emitted them contiguously — so committing
 * any cell re-indexed everything below the first gap. Typing into row 100 of a
 * 183-row grid moved that value to row 2 and dragged the rest up with it, which
 * looks exactly like the editor losing your work. It also makes a row index too
 * unstable to select or scroll to, since the coordinate can mean a different
 * row by the time the commit lands.
 *
 * Only list-bound, computed and linked columns are excluded from the blank
 * test — their cells aren't user-owned, so they must not keep an otherwise
 * empty row alive. Pruning runs on COMMIT, never mid-keystroke, so a row you
 * just made can't vanish from under the cursor.
 */
function isOwned(f: TaxonomyField): boolean {
  return !f.listRef?.listId && f.type !== 'computed' && f.type !== 'linked';
}

/** The row count pruning works on: the longest column the user owns. */
export function ownedRowCount(fields: TaxonomyField[]): number {
  return fields.filter(isOwned).reduce((m, f) => Math.max(m, f.values.length), 0);
}

export function pruneEmptyRows(next: TaxonomyField[]): TaxonomyField[] {
  const owned = next.filter(isOwned);
  if (owned.length === 0) return next;

  const rowCount = ownedRowCount(owned);
  const isBlank = (r: number) =>
    owned.every((f) => {
      const v = f.values[r];
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cell lookups index fields[] / resolvedLists by a coordinate that can fall outside
      return !v || isBlankValue(v);
    });

  let lastFilled = rowCount - 1;
  while (lastFilled >= 0 && isBlank(lastFilled)) lastFilled -= 1;
  const wanted = lastFilled + 2; // every filled row, plus one trailing blank

  // Returning the same array lets React bail out instead of re-rendering the
  // grid on every keystroke.
  if (wanted === rowCount) return next;

  return next.map((f) =>
    owned.includes(f)
      ? {
          ...f,
          values: Array.from({ length: wanted }, (_, r) => f.values[r] ?? { ...EMPTY_VALUE }),
        }
      : f,
  );
}
